import React, { useState, useEffect } from 'react';

interface QSMRow {
  coin: string;
  direction: string;
  tier_score: number;
  gates_passed: string;
  gates_ok: number;
  pair_ok?: number;          // 9-3 and 60-10 at opposite extremes (board API)
  sma_pct?: number;          // signed % of price from SMA20 (board API)
  rank: number;
  px: number;
  sma20: number;
}

interface QSMBoard {
  cycle_ts: number;
  timestamp: string;
  state?: string;            // LIVE | STALE | WARMING (board feed state)
  age_s?: number;            // seconds since the newest scan_live eval_ts
  rows: QSMRow[];
}

// Fallback only — used when a row's gates_passed cannot be parsed at all.
// The live gate set comes from the API string, so adding a gate server-side
// needs no change here.
const GATES_FALLBACK = ['fast', 'medium', 'slow', 'sma', 'll'];

const parseGates = (s: string): { [k: string]: boolean } => {
  const out: { [k: string]: boolean } = {};
  (s || '').split(',').forEach((part) => {
    const [k, v] = part.split('=');
    if (k) out[k.trim()] = v === '1';
  });
  return out;
};

// Keys in the order the API sent them (object insertion order).
const gateKeys = (g: { [k: string]: boolean }): string[] => {
  const keys = Object.keys(g);
  return keys.length ? keys : GATES_FALLBACK;
};

// NEAR now means "the band pair is right but the gates are not yet complete",
// not "one gate short". A row without pair_ok falls through to WATCHING.
const statusFor = (gatesOk: number, total: number, pairOk?: number) => {
  if (total > 0 && gatesOk >= total) return { label: 'ENTER', bg: '#00c853', fg: '#fff' };
  if (pairOk === 1 && gatesOk < total) return { label: 'NEAR', bg: '#ffb300', fg: '#000' };
  return { label: 'WATCHING', bg: '#9e9e9e', fg: '#fff' };
};

// One column track set, shared by the header and every row, so row 1 cannot
// drift out of alignment with the rest.
const dotsWidth = (n: number) => n * 18 + Math.max(0, n - 1) * 4;
const gridFor = (n: number) =>
  `72px 92px 58px 46px ${dotsWidth(n)}px 58px 58px 1fr`;
const dotGridFor = (n: number) => `repeat(${n}, 18px)`;
const GATE_LABELS: { [k: string]: string } = {
  fast: 'F', medium: 'M', slow: 'S', fourth: '14', sma: 'SMA', ll: 'LL'
};
const gateLabel = (k: string) => GATE_LABELS[k] || k;

// /qsm/positions (qsm_board.py:105) — paper trades, DB read only.
interface OpenPos { coin: string; dir: string; entry_px: number; entry_time: string | null;
  max_fav_pct: number | null; max_adv_pct: number | null; poll_age_s: number | null; }
interface ClosedPos { coin: string; dir: string; exit_time: string | null;
  exit_reason: string | null; pct: number | null; }
interface Positions { open: OpenPos[]; closed: ClosedPos[]; }
const fmtPct = (v: number | null) => (v == null ? '—' : (v > 0 ? '+' : '') + v.toFixed(2) + '%');
const fmtUtc = (s: string | null) => (s ? s.slice(5, 16).replace('T', ' ') : '—');
const cellStyle: React.CSSProperties = { padding: '2px 8px', textAlign: 'left' };

const QSMPanel: React.FC = () => {
  const [board, setBoard] = useState<QSMBoard | null>(null);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch('/qsm/board');
        if (!r.ok) throw new Error('http ' + r.status);
        const d = await r.json();
        if (!alive) return;
        setBoard(d);
        setOffline(false);
      } catch (e) {
        if (!alive) return;
        setOffline(true);
      }
    };
    load();
    const iv = setInterval(load, 10000);   // local SQLite read, no HL cost
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const [pos, setPos] = useState<Positions | null>(null);
  const [posOff, setPosOff] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch('/qsm/positions');
        if (!r.ok) throw new Error('http ' + r.status);
        const d = await r.json();
        if (!alive) return;
        setPos(d);
        setPosOff(false);
      } catch (e) {
        if (!alive) return;
        setPosOff(true);
      }
    };
    load();
    const iv = setInterval(load, 10000);   // local SQLite read, no HL cost
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // (4) feed state comes from the board (scan_live eval_ts), not a local
  // 600s guess. OFFLINE stays purely a fetch failure.
  const state = board?.state ?? (board ? 'STALE' : '');
  const stale = state === 'STALE';
  // (2) never render a 0/null cycle_ts as a 1970 timestamp.
  const cycleLocal = (board && board.cycle_ts)
    ? new Date(board.cycle_ts * 1000).toLocaleTimeString() : '—';

  const flagStyle: React.CSSProperties = {
    marginLeft: '10px', padding: '2px 8px', borderRadius: '4px',
    fontSize: '0.75em', fontWeight: 'bold', color: '#fff'
  };

  return (
    <div style={{
      marginTop: '20px', padding: '15px', border: '1px solid #ddd',
      borderRadius: '8px', backgroundColor: '#fafafa', color: '#1a1a1a'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '1.05em' }}>🎯 QSM Scanner Board</h3>
        <span style={{ marginLeft: '12px', fontSize: '0.85em', color: '#555' }}>
          cycle {cycleLocal}
        </span>
        {offline && <span style={{ ...flagStyle, backgroundColor: '#d32f2f' }}>OFFLINE</span>}
        {!offline && board && (
          <span style={{
            ...flagStyle,
            backgroundColor: state === 'LIVE' ? '#00c853'
              : state === 'STALE' ? '#f57c00' : '#9e9e9e',
            color: state === 'STALE' ? '#000' : '#fff'
          }}>
            {state}{board.age_s != null ? ` ${board.age_s}s` : ''}
          </span>
        )}
      </div>

      {!board && !offline && <div style={{ color: '#777' }}>loading…</div>}
      {offline && !board && <div style={{ color: '#d32f2f' }}>no data — scanner unreachable</div>}

      {board && board.rows.length === 0 && (
        <div style={{ color: '#777', padding: '8px 10px', fontSize: '0.9em' }}>
          No setups — waiting for 9-3 / 60-10 opposite + near SMA20
        </div>
      )}

      {board && board.rows.length > 0 && (() => {
        const hdr = gateKeys(parseGates(board.rows[0].gates_passed));
        return (
          <div style={{
            display: 'grid', gridTemplateColumns: gridFor(hdr.length),
            alignItems: 'center',
            gap: '10px', padding: '0 10px 4px 10px',
            fontSize: '0.62em', fontWeight: 'bold', color: '#777',
            letterSpacing: '0.04em'
          }}>
            <span /><span /><span /><span />
            <span style={{ display: 'grid', gridTemplateColumns: dotGridFor(hdr.length), gap: '4px' }}>
              {hdr.map((name) => (
                <span key={name} style={{ textAlign: 'center' }}>{gateLabel(name)}</span>
              ))}
            </span>
            <span /><span>% SMA</span><span />
          </div>
        );
      })()}

      {board && board.rows.map((row, i) => {
        const g = parseGates(row.gates_passed);
        const keys = gateKeys(g);
        const st = statusFor(row.gates_ok, keys.length, row.pair_ok);
        const best = i === 0;
        return (
          <div key={row.coin + i} style={{
            display: 'grid', gridTemplateColumns: gridFor(keys.length),
            alignItems: 'center', gap: '10px',
            padding: '8px 10px', marginBottom: '5px', borderRadius: '5px',
            backgroundColor: best ? '#fffde7' : '#fff',
            border: best ? '2px solid #fbc02d' : '1px solid #eee',
            opacity: offline || stale ? 0.55 : 1
          }}>
            <span style={{
              fontSize: '0.68em', fontWeight: 'bold', color: '#f57f17'
            }}>{best ? 'BEST ENTRY' : ''}</span>
            <span style={{ fontWeight: 'bold', fontSize: '1.15em', color: '#1a1a1a' }}>{row.coin}</span>
            <span style={{
              padding: '2px 8px', borderRadius: '4px', fontSize: '0.78em',
              fontWeight: 'bold', color: '#fff', textAlign: 'center',
              backgroundColor: row.direction === 'LONG' ? '#2e7d32' : '#c62828'
            }}>{row.direction}</span>
            <span style={{ fontSize: '0.85em', color: '#1a1a1a' }}>{row.gates_ok}/{keys.length}</span>
            <span style={{ display: 'grid', gridTemplateColumns: dotGridFor(keys.length), gap: '4px', justifyItems: 'center' }}>
              {keys.map((name) => (
                <span key={name} title={`${name}: ${g[name] ? 'pass' : 'fail'}`} style={{
                  width: '14px', height: '14px', borderRadius: '50%',
                  display: 'inline-block',
                  backgroundColor: g[name] ? '#43a047' : '#e0e0e0',
                  border: '1px solid #bbb'
                }} />
              ))}
            </span>
            <span style={{ fontSize: '0.85em', color: '#555' }}>
              {row.tier_score.toFixed(1)}
            </span>
            <span style={{ fontSize: '0.85em', color: '#1a1a1a' }}>
              {row.sma_pct == null ? '—'
                : `${row.sma_pct >= 0 ? '+' : ''}${row.sma_pct.toFixed(2)}%`}
            </span>
            <span style={{
              justifySelf: 'end', padding: '3px 10px', borderRadius: '4px',
              fontSize: '0.75em', fontWeight: 'bold',
              backgroundColor: st.bg, color: st.fg
            }}>{st.label}</span>
          </div>
        );
      })}

      <div style={{ marginTop: '14px', fontSize: '0.8em' }}>
        <b>Paper positions</b>{posOff && <span style={{ color: '#c33' }}> (offline)</span>}
        <table style={{ borderCollapse: 'collapse', marginTop: '4px' }}><tbody>
          <tr style={{ color: '#888' }}>{['coin', 'dir', 'entry px', 'entry UTC', 'fav', 'adv', 'poll age']
            .map(h => <td key={h} style={cellStyle}>{h}</td>)}</tr>
          {(pos?.open ?? []).length === 0 && <tr><td style={cellStyle} colSpan={7}>no open trades</td></tr>}
          {(pos?.open ?? []).map(p => (
            <tr key={p.coin + p.entry_time}>
              <td style={cellStyle}>{p.coin}</td><td style={cellStyle}>{p.dir}</td>
              <td style={cellStyle}>{p.entry_px}</td><td style={cellStyle}>{fmtUtc(p.entry_time)}</td>
              <td style={cellStyle}>{fmtPct(p.max_fav_pct)}</td><td style={cellStyle}>{fmtPct(p.max_adv_pct)}</td>
              <td style={cellStyle}>{p.poll_age_s == null ? '—' : p.poll_age_s + 's'}</td>
            </tr>))}
        </tbody></table>
        <b style={{ display: 'block', marginTop: '8px' }}>Last 10 closed (gross %, no fees)</b>
        <table style={{ borderCollapse: 'collapse', marginTop: '4px' }}><tbody>
          <tr style={{ color: '#888' }}>{['coin', 'dir', 'exit UTC', 'exit', 'gross %']
            .map(h => <td key={h} style={cellStyle}>{h}</td>)}</tr>
          {(pos?.closed ?? []).map(p => (
            <tr key={p.coin + p.exit_time}>
              <td style={cellStyle}>{p.coin}</td><td style={cellStyle}>{p.dir}</td>
              <td style={cellStyle}>{fmtUtc(p.exit_time)}</td><td style={cellStyle}>{p.exit_reason ?? '—'}</td>
              <td style={cellStyle}>{fmtPct(p.pct)}</td>
            </tr>))}
        </tbody></table>
      </div>

      <div style={{ marginTop: '8px', fontSize: '0.72em', color: '#888' }}>
        gates from the scanner — observation only, no orders placed
      </div>
    </div>
  );
};

export default QSMPanel;
